import React, { useEffect, useState } from 'react';
import InboundV from './Inbound.v';
import ReceptionModal from '../_common/modal/ReceptionModal';
import PageUtil from 'utils/Page.util';
import moment from 'moment';
import { withRouter } from 'core/withRouter';
import { dateFormat } from 'utils/Date.util';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import receptionService from 'services/reception.service';
import kpiService from 'services/kpi.service';
import { DateType, getFlowType, getInboundMethod, getReceptionStatus, getReceptionType, ReceptionItemKind, ReceptionStatus, ReceptionType } from 'enums/reception.enum';
import { InboundKpiDTO } from 'models/kpi';

/** 내부 상태 모델 */
class Params {
    error: string | null = null;
    isLoaded = false;
    total: number = 0;

    datePickerMode: 'daily' | 'monthly' = 'daily';

    list: ReceptionDTO[] = [];
    search: ReceptionSearchDTO = new ReceptionSearchDTO();

    // KPI 데이터 추가
    kpiData: InboundKpiDTO = new InboundKpiDTO();
    kpiLoaded = false;

    statusSet = getReceptionStatus();
    typeSet = ['일반', '맞교환', '출장', '보상판매', '방문', '판매'];
}

function Inbound() {
    const [params, setParams] = useState(new Params());
    const [search, setSearch] = useState(false);

    // 모달 상태 관리 추가
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingRow, setEditingRow] = useState<ReceptionDTO | null>(null);

    /** 초기 진입 시 기본 검색기간(최근 30일) 설정 + 조회 */
    useEffect(() => {
        const cur = PageUtil.deepCopy(params) as Params;
        // cur.search.status = ReceptionStatus.INBOUND_SCHEDULED;
        // cur.search.ignoreStatus = [ReceptionStatus.DONE, ReceptionStatus.PAID].join(',');
        // cur.search.ignoreTypes = [ReceptionType.EXCHANGE, ReceptionType.NEW_EXCHANGE].join(',');
        cur.search.dateType = DateType.INBOUND;
        cur.search.startDate = dateFormat(new Date(new Date().setDate(new Date().getDate() - 1)), 'YYYY-MM-DD');
        cur.search.endDate = dateFormat(new Date(), 'YYYY-MM-DD');
        cur.isLoaded = false;
        setParams(cur);
        // setSearch(true);
        getList(cur.search);

        // KPI 데이터 조회
        loadKPIData();
    }, []);

    /** 검색 트리거 */
    useEffect(() => {
        if (!search) return;
        setSearch(false);
        getList(params.search);
    }, [search]);

    /** 목록 조회 */
    async function getList(search: ReceptionSearchDTO) {
        try {
            console.log("search", search);
            const res = await receptionService.list(search);
            console.log("res", res);
            setParams((prev) => ({
                ...prev,
                list: res.content || [],
                total: res.totalElements || 0,
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    /** KPI 데이터 조회 */
    async function loadKPIData() {
        try {
            const kpiData = await kpiService.inbound();
            console.log("kpiData", kpiData);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
                kpiData,
            }));
        } catch (error) {
            console.error('KPI 데이터 조회 실패:', error);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
            }));
        }
    }

    /** 검색 버튼 */
    function onSearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로
        setParams(cur);
        setSearch(true);
    }

    /** 초기화 */
    function onClearFilter() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.dateType = DateType.RECEIVED;
        cur.search.types = "";
        cur.search.keyword = '';
        cur.search.flowType = "";
        cur.search.startDate = dateFormat(new Date(new Date().setDate(new Date().getDate() - 30)), 'YYYY-MM-DD');
        cur.search.endDate = dateFormat(new Date(), 'YYYY-MM-DD');
        cur.search.page = 1;
        cur.isLoaded = false;
        setParams(cur);
        setSearch(true);
    }

    /** Today 버튼 - 당일 데이터만 조회 */
    function onTodaySearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        const today = dateFormat(new Date(), 'YYYY-MM-DD');

        cur.search.dateType = DateType.INBOUND;
        cur.search.startDate = today;
        cur.search.endDate = today;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로

        setParams(cur);
        setSearch(true);
    }

    /** DAY/MONTH 토글 */
    function onToggleDatePickerMode(mode: 'daily' | 'monthly') {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.datePickerMode = mode;
        setParams(cur);
    }

    /** 날짜 변경 핸들러 */
    function onHandleSearchDate(value: string, col: 'startDate' | 'endDate') {
        const cur = PageUtil.deepCopy(params) as Params;

        if (params.datePickerMode === 'monthly') {
            if (col === 'startDate') {
                cur.search[col] = moment(value, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
            } else if (col === 'endDate') {
                cur.search[col] = moment(value, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
            }
        } else {
            cur.search[col] = value;
        }

        setParams(cur);
    }

    /** 행 상태 변경 → 즉시 저장(간단 예시) */
    async function onChangeStatus(row: ReceptionDTO, newStatus: ReceptionStatus) {
        if (!confirm("처리상황을 변경하시겠습니까?")) return;
        try {
            await receptionService.updateStatus({ id: row.id, status: newStatus });
            onSearch();
        } catch (error) {
            PageUtil.handleApiError(error);
        }
    }

    async function onSaveInboundDate(row: ReceptionDTO) {
        if (!confirm("일괄 입고처리 하시겠습니까?")) return;
        try {
            await receptionService.updateInboundDate(row.id);
            onSearch();
        } catch (error) {
            PageUtil.handleApiError(error);
        }
    }

    /** 신규 접수 모달 열기 */
    function onOpenCreate() {
        setModalMode('create');
        setEditingRow(null);
        setShowModal(true);
    }

    /** 수정 모달 열기 */
    function onOpenEdit(row: ReceptionDTO) {
        setModalMode('edit');
        setEditingRow(row);
        setShowModal(true);
    }

    /** 모달에서 저장 완료 시 */
    async function onModalSave(reception: ReceptionDTO, close: boolean = true) {
        try {
            console.log("reception", reception);
            await receptionService.save(reception);
            if (close) setShowModal(false);
            setSearch(true); // 목록 새로고침
            alert('저장되었습니다.');
            // 필요시 목록 새로고침 등 추가 처리
        } catch (error) {
            console.error('접수 저장 실패:', error);
            alert('저장에 실패했습니다.');
        }
    }

    /** 모달 닫기 */
    function onModalClose() {
        setShowModal(false);
        setEditingRow(null);
    }

    const handleExportExcel = async () => {

        const cur = PageUtil.deepCopy(params) as Params;
        const search = cur.search;
        search.rows = 9999;
        search.page = 1;
        const res = await receptionService.list(search);

        try {
            const excelData = res?.content?.map((row) => {
                const boilerItem = row.items?.find(p => p.kind === ReceptionItemKind.BOILER);

                return {
                    '처리상황': getReceptionStatus().find(s => s.value === row.status)?.label || row.status || '',
                    '처리유형': getReceptionType().find(t => t.value === row.type)?.label || row.type || '',
                    '접수일자': row.receivedDate || '',
                    '택배입고일': row.items?.filter((item) => item.courierInboundDate).map(item => item.courierInboundDate).join(', ') || "",
                    '연락처': row.phone1 || '',
                    '고객명': row.customerName || '',
                    '주소': (row.province || '') + " " + (row.city || '') + " " + (row.address || ''),
                    '보일러명': boilerItem?.modelName || '',
                    '제조년월': row?.items?.find(p => p.manufactureMonth)?.manufactureMonth || '',
                    '특이사항': row.note || '',
                    '입고경로': getInboundMethod().find((m) => m.value === boilerItem?.inboundMethod)?.label || boilerItem?.inboundMethod || '',
                    '입고/택발': getFlowType().find((f) => f.value === boilerItem?.flow)?.label || boilerItem?.flow || '',
                };
            });

            const today = PageUtil.getCurrentDate();
            let filename = `입고관리_${today}`;

            if (search.startDate && search.endDate) {
                filename += `_${search.startDate}부터${search.endDate}까지`;
            }
            if (search.keyword) {
                filename += `_${search.keyword}`;
            }
            filename += '.xlsx';

            PageUtil.exportToExcel(excelData, filename, '입고관리');

        } catch (error) {
            console.error('엑셀 내보내기 오류:', error);
            alert('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };

    const args = {
        params,
        setParams,
        setSearch,

        handleChange: PageUtil.handleChange,
        onSearch,
        onClearFilter,
        onTodaySearch, // Today 버튼 핸들러 추가
        onToggleDatePickerMode,
        onHandleSearchDate,

        onOpenCreate,
        onOpenEdit,

        onChangeStatus,
        onSaveInboundDate,
        handleExportExcel
    };

    return (
        <>
            <InboundV {...args} />

            {/* 접수 등록/수정 모달 */}
            <ReceptionModal
                show={showModal}
                title={modalMode === 'create' ? '신규 접수 등록' : '접수 수정'}
                initial={editingRow}
                onSave={onModalSave}
                onClose={onModalClose}
            />
        </>
    );
}

export default withRouter(Inbound);